import type { OperationModeMap } from '../../shell/session/domain/operationMode';
import type { OtherRubroOperationTemplate } from './otherRubroFlow';

/** Feature ids shown in Otros (owner language → capabilities / ERP). */
export type OtherFeatureOptionId =
  | 'catalog_stock'
  | 'weight_uom'
  | 'product_variants'
  | 'fiado'
  | 'promotions'
  | 'appointments'
  | 'service_quotes'
  | 'work_orders'
  | 'kitchen_tickets'
  | 'tables'
  | 'online_orders';

export type OtherFeatureOption = {
  id: OtherFeatureOptionId;
  label: string;
  hint: string;
  capability_codes: string[];
};

export type OtherFeatureGroup = {
  id: string;
  label: string;
  options: readonly OtherFeatureOption[];
};

/** Default catalog when RPC `list_onboarding_picker` is unavailable. */
export const DEFAULT_OTHER_FEATURE_GROUPS: readonly OtherFeatureGroup[] = [
  {
    id: 'productos',
    label: 'Productos',
    options: [
      {
        id: 'catalog_stock',
        label: 'Catálogo y stock',
        hint: 'Precios e inventario en el local.',
        capability_codes: ['catalog', 'inventory'],
      },
      {
        id: 'weight_uom',
        label: 'Venta por kilo',
        hint: 'Balanza y precio por peso.',
        capability_codes: ['weight_uom'],
      },
      {
        id: 'product_variants',
        label: 'Talles y colores',
        hint: 'Variantes: talle, color, modelo.',
        capability_codes: ['product_variants'],
      },
    ],
  },
  {
    id: 'clientes',
    label: 'Clientes',
    options: [
      {
        id: 'fiado',
        label: 'Clientes',
        hint: 'Cuentas y cobros a crédito.',
        capability_codes: ['customer_credit_fiado'],
      },
      {
        id: 'promotions',
        label: 'Promos',
        hint: 'Ofertas, 2x1 y combos.',
        capability_codes: ['promotions'],
      },
    ],
  },
  {
    id: 'turnos',
    label: 'Agenda y oficios',
    options: [
      {
        id: 'appointments',
        label: 'Agenda',
        hint: 'Turnos y reservas.',
        capability_codes: ['appointments'],
      },
      {
        id: 'service_quotes',
        label: 'Presupuestos',
        hint: 'Cotizaciones para el cliente.',
        capability_codes: ['service_quotes'],
      },
      {
        id: 'work_orders',
        label: 'Órdenes de taller',
        hint: 'Reparaciones en curso.',
        capability_codes: ['work_orders'],
      },
    ],
  },
  {
    id: 'comida',
    label: 'Cocina y salón',
    options: [
      {
        id: 'kitchen_tickets',
        label: 'Comandas',
        hint: 'Pedidos a cocina.',
        capability_codes: ['kitchen_tickets'],
      },
      {
        id: 'tables',
        label: 'Mesas',
        hint: 'Salón con mesas y mozos.',
        capability_codes: ['tables'],
      },
    ],
  },
  {
    id: 'online',
    label: 'Online',
    options: [
      {
        id: 'online_orders',
        label: 'Pedidos online',
        hint: 'Tienda por internet.',
        capability_codes: ['delivery_fulfillment'],
      },
    ],
  },
] as const;

const FOOD_IDS: ReadonlySet<OtherFeatureOptionId> = new Set(['kitchen_tickets', 'tables']);
const WORKSHOP_IDS: ReadonlySet<OtherFeatureOptionId> = new Set(['work_orders']);
const APPOINTMENTS_IDS: ReadonlySet<OtherFeatureOptionId> = new Set([
  'appointments',
  'service_quotes',
]);

/** Nearest ERP template from selected features. */
export function resolveTemplateFromOtherFeatures(
  selected: readonly OtherFeatureOptionId[],
): OtherRubroOperationTemplate {
  if (selected.some((id) => FOOD_IDS.has(id))) return 'food';
  if (selected.some((id) => WORKSHOP_IDS.has(id))) return 'workshop';
  if (selected.some((id) => APPOINTMENTS_IDS.has(id))) return 'appointments';
  return 'retail';
}

/** storefront-only operation_mode patch from Otros feature picks. */
export function buildOperationModePatchFromOtherFeatures(
  selected: readonly OtherFeatureOptionId[],
): OperationModeMap {
  const set = new Set(selected);
  return { storefront: set.has('online_orders') };
}

/** Capabilities to disable via overrides when the user did not pick them in Otros. */
export function buildOnboardingCapabilityDisables(
  selected: readonly OtherFeatureOptionId[],
): Array<'customer_credit_fiado' | 'promotions'> {
  const set = new Set(selected);
  const out: Array<'customer_credit_fiado' | 'promotions'> = [];
  if (!set.has('fiado')) out.push('customer_credit_fiado');
  if (!set.has('promotions')) out.push('promotions');
  return out;
}

export function toggleOtherFeature(
  selected: readonly OtherFeatureOptionId[],
  id: OtherFeatureOptionId,
): OtherFeatureOptionId[] {
  if (selected.includes(id)) return selected.filter((x) => x !== id);
  return [...selected, id];
}

export function parseOtherFeatureGroups(raw: unknown): OtherFeatureGroup[] {
  // Copy canónico en DEFAULT_OTHER_FEATURE_GROUPS (voz mostrador).
  // Si el RPC trae grupos, usamos su estructura de ids pero labels/hints del default.
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_OTHER_FEATURE_GROUPS];

  const defaultByOptionId = new Map<string, OtherFeatureOption>();
  for (const g of DEFAULT_OTHER_FEATURE_GROUPS) {
    for (const opt of g.options) defaultByOptionId.set(opt.id, opt);
  }
  const defaultGroupById = new Map(DEFAULT_OTHER_FEATURE_GROUPS.map((g) => [g.id, g]));

  const groups: OtherFeatureGroup[] = [];
  for (const g of raw) {
    if (!g || typeof g !== 'object') continue;
    const go = g as Record<string, unknown>;
    const id = typeof go.id === 'string' ? go.id : null;
    if (!id || !Array.isArray(go.options)) continue;
    const fallbackGroup = defaultGroupById.get(id);
    const options: OtherFeatureOption[] = [];
    for (const opt of go.options) {
      if (!opt || typeof opt !== 'object') continue;
      const o = opt as Record<string, unknown>;
      if (typeof o.id !== 'string') continue;
      const known = defaultByOptionId.get(o.id);
      if (known) {
        options.push(known);
        continue;
      }
      if (typeof o.label !== 'string') continue;
      options.push({
        id: o.id as OtherFeatureOptionId,
        label: o.label,
        hint: typeof o.hint === 'string' ? o.hint : '',
        capability_codes: Array.isArray(o.capability_codes)
          ? o.capability_codes.filter((c): c is string => typeof c === 'string')
          : [],
      });
    }
    if (options.length > 0) {
      groups.push({
        id,
        label: fallbackGroup?.label ?? (typeof go.label === 'string' ? go.label : id),
        options,
      });
    }
  }
  return groups.length > 0 ? groups : [...DEFAULT_OTHER_FEATURE_GROUPS];
}
